import { useState, useCallback, useEffect } from "react";
import { customBaseUrl, httpGet } from "../../../../store/http";
import {
  Row,
  Col,
  Input,
  Button,
  Spin,
  Alert,
  Tag,
  Typography,
  Card,
} from "antd";
import AntTable from "../../../../components/CustomTable/AntTable";
import AdminTransfer from "./admin-transfer-modal";
import { formatNaira } from "../../../dashboard/shared/utils";
import { AlertEmpty } from "./alert-empty";

const { Title } = Typography;

const getStatusColor = (status) => {
  switch (status) {
    case "SUCCESS":
      return "success";
    case "PENDING":
      return "warning";
    case "FAILED":
      return "error";
    default:
      return "default";
  }
};

export const Transactions = ({ id, activeTab, userWallets }) => {
  const [referenceFilter, setReferenceFilter] = useState("");
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [isFilteringByReference, setIsFilteringByReference] = useState(false);
  const [isTransferModalVisible, setIsTransferModalVisible] = useState(false);
  const [transactionsError, setTransactionsError] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionsPagination, setTransactionsPagination] = useState({
    current: 0,
    pageSize: 5,
  });

  const fetchTransactionByReference = useCallback(async (reference) => {
    if (!reference) return;
    setTransactionsLoading(true);
    setTransactionsError(null);
    try {
      const res = await httpGet(
        `${customBaseUrl.tranv2}/api/v1/admin/transactions/reference/${reference}`,
      );
      if (res?.success) {
        setTransactions(res.data ? [res.data] : []);
        setTransactionsPagination((prev) => ({
          ...prev,
          total: res.data ? 1 : 0,
          current: 0,
        }));
        setIsFilteringByReference(true);
      } else {
        setTransactionsError(res?.message || "Transaction not found.");
      }
    } catch (err) {
      setTransactionsError(
        "An error occurred while fetching transaction by reference.",
      );
      console.error(err);
    } finally {
      setTransactionsLoading(false);
    }
  }, []);

  const handleClearSearch = () => {
    setReferenceFilter("");
    setIsFilteringByReference(false);
    fetchTransactions(0);
  };

  const current = transactionsPagination.current;
  const pageSize = transactionsPagination.pageSize;

  const fetchTransactions = useCallback(
    async (page = current, size = pageSize) => {
      setTransactionsLoading(true);

      try {
        const res = await httpGet(
          `${customBaseUrl.tranv2}/api/v1/admin/transactions/user/${id}?page=${page}&size=${size}`,
        );
        if (res?.success) {
          setTransactions(res.data.content || []);
          setTransactionsPagination((prev) => ({
            ...prev,
            total: res.data.totalElements,
            current: Number.isInteger(res.data.number)
              ? res.data.number
              : prev.current,
          }));
        } else {
          setTransactionsError(res?.message || "Failed to fetch transactions.");
        }
      } catch (err) {
        setTransactionsError("An error occurred while fetching transactions.");
        console.error(err);
      } finally {
        setTransactionsLoading(false);
      }
    },
    [id, current, pageSize],
  );

  useEffect(() => {
    if (activeTab === "transactions") {
      fetchTransactions();
    }
  }, [activeTab, fetchTransactions]);

  const columns = [
    {
      title: "Transaction ID",
      dataIndex: "transactionId",
    },
    {
      title: "Reference",
      dataIndex: "reference",
    },
    {
      title: "Type",
      dataIndex: "transactionType",
    },
    {
      title: "Nature",
      dataIndex: "transactionNature",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (val) => <strong>{formatNaira(val)}</strong>,
    },
    {
      title: "Fee",
      dataIndex: "fee",
      render: (val) => formatNaira(val),
    },
    {
      title: "From",
      dataIndex: "fromAccountNumber",
    },
    {
      title: "To",
      dataIndex: "toAccountNumber",
    },
    {
      title: "Receiver",
      dataIndex: "receiverName",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (val) => (
        <Tag
          color={getStatusColor(val)}
          style={{ fontWeight: 500, padding: "2px 8px" }}
        >
          {val}
        </Tag>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (val) => new Date(val).toLocaleString(),
    },
  ];

  return (
    <Card
      style={{
        borderRadius: 12,
        padding: 20,
      }}
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            Transactions
          </Title>
        </Col>

        <Col>
          <Button
            type="default"
            size="small"
            onClick={() => setIsTransferModalVisible(true)}
          >
            Make Transfer
          </Button>
        </Col>
      </Row>

      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col>
          <Input
            placeholder="Search by Reference ID"
            value={referenceFilter}
            onChange={(e) => setReferenceFilter(e.target.value)}
            style={{ width: 280 }}
            allowClear
          />
        </Col>

        <Col>
          <Button
            type="default"
            style={{ height: "100%" }}
            onClick={() => fetchTransactionByReference(referenceFilter)}
            loading={transactionsLoading}
          >
            Search
          </Button>
        </Col>

        {isFilteringByReference && (
          <Col>
            <Button onClick={handleClearSearch}>Reset</Button>
          </Col>
        )}
      </Row>

      <AdminTransfer
        userId={id}
        userWallets={userWallets}
        visible={isTransferModalVisible}
        onClose={() => setIsTransferModalVisible(false)}
        onTransferSuccess={() => {
          setIsTransferModalVisible(false);
          fetchTransactions();
        }}
      />

      <div style={{ marginTop: 12 }}>
        {transactionsLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin tip="Loading transactions..." />
          </div>
        ) : transactionsError ? (
          <Alert
            message="Error loading transactions"
            description={transactionsError}
            type="error"
            showIcon
          />
        ) : transactions.length > 0 ? (
          <AntTable
            users={transactions}
            columns={columns}
            rowKey="id"
            pagination={{
              ...transactionsPagination,
              current: transactionsPagination.current + 1,
            }}
            handleGetNext={({ pagination }) =>
              setTransactionsPagination((prev) => {
                const isSizeChanged = prev.pageSize !== pagination.pageSize;

                return {
                  ...prev,
                  pageSize: pagination.pageSize,
                  current: isSizeChanged ? 0 : pagination.current - 1,
                };
              })
            }
          />
        ) : (
          <AlertEmpty text="Sorry, no transactions found." />
        )}
      </div>
    </Card>
  );
};
